import React, { useState, useEffect, useRef } from 'react';
import { getDomainConfig } from '../config/domainConfig';

const PublicComplaint = () => {
  const domainConfig = getDomainConfig();
  const [tenantInfo, setTenantInfo] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    customer_name: '', customer_phone: '', customer_email: '',
    customer_address: '', connection_id: '', category: '', description: '', priority: 'medium'
  });
  const [lcoSearch, setLcoSearch] = useState('');
  const [lcos, setLcos] = useState([]);
  const [filteredLcos, setFilteredLcos] = useState([]);
  const [selectedLco, setSelectedLco] = useState(null);
  const [showLcoDropdown, setShowLcoDropdown] = useState(false);
  const [bmsLoading, setBmsLoading] = useState(false);
  const [bmsFound, setBmsFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState('');
  const lcoRef = useRef(null);

  const tenantCode = window.location.pathname.split('/complaint/')[1]?.split('/')[0] || domainConfig.tenant_code || 'PDIC';
  const primary = domainConfig.primary_color || tenantInfo?.primary_color || '#1B6B3A';

  useEffect(() => {
    // Fetch tenant info and LCOs from single endpoint
    fetch(`/api/network/public/${tenantCode}/lcos`)
      .then(r => r.json()).then(d => { 
        if (d.success) {
          setLcos(d.lcos || []);
          if (d.tenant) setTenantInfo(d.tenant.branding || d.tenant);
        }
      }).catch(() => {});
    const handleClick = (e) => { if (lcoRef.current && !lcoRef.current.contains(e.target)) setShowLcoDropdown(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (!lcoSearch || lcoSearch.length < 2) { setFilteredLcos([]); return; }
    setFilteredLcos(lcos.filter(l =>
      l.name?.toLowerCase().includes(lcoSearch.toLowerCase()) ||
      l.area?.area_name?.toLowerCase().includes(lcoSearch.toLowerCase()) ||
      l.area?.pincodes?.some(p => p.includes(lcoSearch))
    ).slice(0, 10));
  }, [lcoSearch, lcos]);

  const categories = ['No Internet Connection','Slow Speed','WiFi Issue','Frequent Disconnection','Billing Issue','New Connection Request','Relocation Request','Others'];
  const priorities = [
    { value:'low', label:'Low', desc:'General query', color:'#10b981' },
    { value:'medium', label:'Medium', desc:'Service degraded', color:'#f59e0b' },
    { value:'high', label:'High', desc:'No service', color:'#ef4444' },
  ];

  const handleSubmit = async () => {
    if (!form.customer_name || !form.customer_phone || !form.category || !form.description) {
      setError('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = { tenant_code: tenantCode, ...form,
        lco_id: selectedLco?._id || null,
        source: 'public_url'
      };
      const r = await fetch('/api/tickets/public', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const d = await r.json();
      if (d.success) setSubmitted(d);
      else setError(d.message || 'Could not submit complaint');
    } catch(e) { setError('Network error. Please try again.'); }
    setSubmitting(false);
  };

  const inp = { width:'100%', padding:'11px 14px', border:'1.5px solid #e2e8f0', borderRadius:'10px', fontSize:'14px', color:'#1e293b', background:'white', outline:'none', boxSizing:'border-box', fontFamily:'inherit', transition:'border-color .2s' };

  const logoUrl = domainConfig.logo_url
    ? (domainConfig.logo_url.startsWith('http') ? domainConfig.logo_url : window.location.origin + domainConfig.logo_url)
    : (tenantInfo?.logo_url ? window.location.origin + tenantInfo.logo_url : null);

  if (submitted) return (
    <div style={{ minHeight:'100vh', background:'#f0f4f8', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Segoe UI',sans-serif", padding:'24px' }}>
      <div style={{ background:'white', borderRadius:'20px', padding:'48px', maxWidth:'480px', width:'100%', textAlign:'center', boxShadow:'0 20px 60px rgba(0,0,0,0.08)' }}>
        <div style={{ width:'72px', height:'72px', background:`${primary}15`, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'32px', margin:'0 auto 24px' }}>✅</div>
        <h2 style={{ margin:'0 0 8px', fontSize:'24px', fontWeight:'800', color:'#0f172a' }}>Complaint Registered!</h2>
        <p style={{ margin:'0 0 24px', color:'#64748b', fontSize:'14px', lineHeight:1.6 }}>Your complaint has been registered successfully. Our team will contact you shortly.</p>
        <div style={{ background:`${primary}08`, border:`1px solid ${primary}20`, borderRadius:'12px', padding:'20px', marginBottom:'24px' }}>
          <p style={{ margin:'0 0 4px', fontSize:'12px', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.5px', fontWeight:'600' }}>Ticket Number</p>
          <p style={{ margin:0, fontSize:'28px', fontWeight:'800', color:primary }}>{submitted.ticket_number}</p>
        </div>
        <p style={{ margin:'0 0 24px', fontSize:'13px', color:'#64748b' }}>Save this ticket number to track your complaint status.</p>
        <button onClick={() => { setSubmitted(null); setStep(1); setForm({ customer_name:'', customer_phone:'', customer_email:'', customer_address:'', connection_id:'', category:'', description:'', priority:'medium' }); setSelectedLco(null); }}
          style={{ padding:'12px 28px', background:primary, color:'white', border:'none', borderRadius:'10px', fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
          Submit Another
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', background:'#f0f4f8', fontFamily:"'Segoe UI',-apple-system,sans-serif" }}>
      <style>{`
        .pinp:focus { border-color:${primary} !important; box-shadow:0 0 0 3px ${primary}15; }
        .pcat:hover { border-color:${primary} !important; background:${primary}08 !important; }
        .pcat.sel { border-color:${primary} !important; background:${primary}10 !important; }
        .ppri:hover { border-color:${primary} !important; }
        .ppri.sel { border-color:${primary} !important; background:${primary}08 !important; }
        .lco-item:hover { background:#f8fafc; }
        .snbtn:hover { opacity:.9; transform:translateY(-1px); }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
      `}</style>

      {/* Header */}
      <div style={{ background:'#0d1f15', padding:'0', position:'relative', overflow:'hidden' }}>
        {/* Dot pattern */}
        <div style={{ position:'absolute', inset:0, backgroundImage:`radial-gradient(circle,rgba(27,107,58,.25) 1px,transparent 1px)`, backgroundSize:'28px 28px', pointerEvents:'none' }} />
        <div style={{ position:'absolute', top:0, right:0, width:'300px', height:'100%', background:`${primary}15`, transform:'skewX(-8deg) translateX(60px)', pointerEvents:'none' }} />

        <div style={{ maxWidth:'960px', margin:'0 auto', padding:'20px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'relative', zIndex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
            {logoUrl ? (
              <div style={{ background:'white', borderRadius:'12px', padding:'10px 16px' }}>
                <img src={logoUrl} alt={tenantInfo?.name} style={{ height:'56px', maxWidth:'180px', objectFit:'contain', display:'block' }} />
              </div>
            ) : (
              <div style={{ width:'44px', height:'44px', background:primary, borderRadius:'10px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'20px' }}>📡</div>
            )}
            <div>
              <p style={{ margin:0, fontSize:'16px', fontWeight:'700', color:'white' }}>{domainConfig.app_name || tenantInfo?.name || 'Support Portal'}</p>
              <p style={{ margin:0, fontSize:'12px', color:'rgba(255,255,255,.5)' }}>Customer Complaint Portal</p>
            </div>
          </div>
          <div style={{ display:'flex', gap:'20px' }}>
            <div style={{ display:'flex', gap:'8px' }}>
              {[{icon:'🎫',label:'Raise Ticket'},{icon:'💬',label:'WhatsApp'},{icon:'📊',label:'Track Status'}].map((item,i) => (
                <div key={i} style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'20px', padding:'5px 14px', fontSize:'12px', color:'rgba(255,255,255,.8)', cursor:'default', backdropFilter:'blur(10px)' }}>
                  {item.icon} {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Hero */}
        <div style={{ maxWidth:'960px', margin:'0 auto', padding:'32px 24px 40px', position:'relative', zIndex:1 }}>
          <div style={{ display:'flex', gap:'8px', marginBottom:'16px', flexWrap:'wrap' }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:'rgba(74,222,128,0.15)', border:'1px solid rgba(74,222,128,0.3)', borderRadius:'20px', padding:'4px 14px', fontSize:'12px', color:'#4ade80', fontWeight:'600', letterSpacing:'.5px' }}>
              <span style={{ width:'6px', height:'6px', background:'#4ade80', borderRadius:'50%', display:'inline-block', animation:'pulse 2s infinite' }}></span> LIVE SUPPORT 24/7
            </div>
            <div style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'20px', padding:'4px 14px', fontSize:'12px', color:'rgba(255,255,255,0.7)', fontWeight:'500' }}>
              ⚡ Response within 24hrs
            </div>
          </div>
          <h1 style={{ margin:'0 0 10px', fontSize:'32px', fontWeight:'800', color:'white', letterSpacing:'-0.5px', lineHeight:1.15 }}>
            Report a Broadband Issue
          </h1>
          <p style={{ margin:'0 0 20px', fontSize:'15px', color:'rgba(255,255,255,.55)', maxWidth:'500px', lineHeight:1.6 }}>
            Submit your complaint and our team will respond within 24 hours. Track your ticket with the number provided.
          </p>
          <div style={{ display:'flex', gap:'20px', flexWrap:'wrap' }}>
            {[{n:'2 Lakh+',l:'Subscribers'},{n:'8',l:'Sub-Districts'},{n:'24hr',l:'Resolution Time'},{n:'99%',l:'Satisfaction'}].map((s,i) => (
              <div key={i} style={{ textAlign:'center' }}>
                <div style={{ fontSize:'20px', fontWeight:'800', color:'white' }}>{s.n}</div>
                <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.5)' }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ maxWidth:'960px', margin:'-20px auto 0', padding:'0 24px 48px', position:'relative', zIndex:2 }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:'20px', alignItems:'start' }}>

          {/* Form Card */}
          <div style={{ background:'white', borderRadius:'16px', boxShadow:'0 4px 24px rgba(0,0,0,0.06)', overflow:'hidden' }}>
            {/* Progress bar */}
            <div style={{ height:'4px', background:'#f1f5f9' }}>
              <div style={{ height:'100%', width:`${step === 1 ? '50%' : '100%'}`, background:primary, transition:'width .4s ease', borderRadius:'2px' }} />
            </div>

            <div style={{ padding:'32px' }}>
              {/* Step indicator */}
              <div style={{ display:'flex', gap:'8px', marginBottom:'28px' }}>
                {[{ n:1, label:'Your Details' },{ n:2, label:'Issue Details' }].map(s => (
                  <div key={s.n} style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <div style={{ width:'28px', height:'28px', borderRadius:'50%', background: step >= s.n ? primary : '#f1f5f9', color: step >= s.n ? 'white' : '#94a3b8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'13px', fontWeight:'700', transition:'all .2s' }}>{s.n}</div>
                    <span style={{ fontSize:'13px', fontWeight: step === s.n ? '700' : '400', color: step === s.n ? '#0f172a' : '#94a3b8' }}>{s.label}</span>
                    {s.n < 2 && <div style={{ width:'32px', height:'1px', background:'#e2e8f0', margin:'0 4px' }} />}
                  </div>
                ))}
              </div>

              {error && (
                <div style={{ padding:'12px 16px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:'10px', marginBottom:'20px', fontSize:'13px', color:'#dc2626', display:'flex', alignItems:'center', gap:'8px' }}>
                  <span>⚠️</span> {error}
                </div>
              )}

              {step === 1 ? (
                <div>
                  <h2 style={{ margin:'0 0 6px', fontSize:'20px', fontWeight:'700', color:'#0f172a' }}>Your Details</h2>
                  <p style={{ margin:'0 0 24px', fontSize:'13px', color:'#64748b' }}>Tell us who you are so we can identify your connection</p>

                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'16px' }}>
                    <div>
                      <label style={{ display:'block', fontSize:'12px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Full Name *</label>
                      <input className="pinp" style={inp} placeholder="Your full name" value={form.customer_name}
                        onChange={e => setForm({...form, customer_name: e.target.value})} />
                    </div>
                    <div>
                      <label style={{ display:'block', fontSize:'12px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Phone Number *</label>
                      <input className="pinp" style={inp} placeholder="10-digit mobile number" value={form.customer_phone}
                        onChange={e => setForm({...form, customer_phone: e.target.value})} maxLength={10} />
                    </div>
                  </div>

                  <div style={{ marginBottom:'16px' }}>
                    <label style={{ display:'block', fontSize:'12px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Email Address</label>
                    <input className="pinp" style={inp} type="email" placeholder="your@email.com (optional)" value={form.customer_email}
                      onChange={e => setForm({...form, customer_email: e.target.value})} />
                  </div>

                  <div style={{ marginBottom:'16px' }}>
                    <label style={{ display:'block', fontSize:'12px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Connection ID / Username</label>
                    <div style={{ position:'relative' }}>
                      <input className="pinp" style={inp} placeholder="Your ISP username or connection ID" value={form.connection_id}
                        onChange={async e => {
                          const uid = e.target.value.trim();
                          setForm(prev=>({...prev, connection_id: uid}));
                          setBmsFound(false);
                          if (uid.length < 5) { setBmsLoading(false); return; }
                          setBmsLoading(true);
                          try {
                            const resp = await fetch('/api/tickets/public/lookup-user', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ tenant_code: tenantCode, user_id: uid })
                            });
                            const data = await resp.json();
                            if (data.success && data.session) {
                              const s = data.session;
                              setBmsFound(true);
                              setForm(prev => ({
                                ...prev,
                                connection_id: uid,
                                customer_name: s.name || prev.customer_name,
                                customer_phone: s.mobile || prev.customer_phone,
                                customer_address: s.sub_dist_name || prev.customer_address
                              }));
                            }
                          } catch(err) { console.log('Lookup error:', err); }
                          setBmsLoading(false);
                        }} />
                      {bmsLoading && <span style={{ position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', fontSize:'11px', color:'#f59e0b', fontWeight:'600' }}>⏳ Searching with ISP...</span>}
                      {bmsFound && !bmsLoading && <span style={{ position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', fontSize:'11px', color:'#10b981', fontWeight:'600' }}>✓ Found</span>}
                    </div>
                  </div>

                  {/* LCO Search */}
                  <div style={{ marginBottom:'24px' }} ref={lcoRef}>
                    <label style={{ display:'block', fontSize:'12px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Your LCO / Area</label>
                    <div style={{ position:'relative' }}>
                      <input className="pinp" style={inp} placeholder="Search by area name or pincode..."
                        value={selectedLco ? selectedLco.name : lcoSearch}
                        onChange={e => { setLcoSearch(e.target.value); setSelectedLco(null); setShowLcoDropdown(true); }}
                        onFocus={() => { if (lcoSearch.length >= 2) setShowLcoDropdown(true); }} />
                      {selectedLco && (
                        <button onClick={() => { setSelectedLco(null); setLcoSearch(''); }} style={{ position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:'16px', color:'#94a3b8' }}>✕</button>
                      )}
                      {showLcoDropdown && filteredLcos.length > 0 && !selectedLco && (
                        <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:'white', border:'1px solid #e2e8f0', borderRadius:'10px', boxShadow:'0 8px 24px rgba(0,0,0,0.1)', zIndex:100, overflow:'hidden' }}>
                          {filteredLcos.map((l,i) => (
                            <div key={i} className="lco-item" onClick={() => { setSelectedLco(l); setForm({...form, customer_address: l.area?.area_name || l.name}); setShowLcoDropdown(false); }}
                              style={{ padding:'12px 16px', cursor:'pointer', borderBottom: i < filteredLcos.length-1 ? '1px solid #f8fafc' : 'none', transition:'background .15s' }}>
                              <div style={{ fontSize:'13px', fontWeight:'600', color:'#1e293b' }}>{l.name}</div>
                              <div style={{ fontSize:'12px', color:'#94a3b8' }}>{l.area?.area_name}{l.area?.pincodes?.length ? ' · ' + l.area.pincodes[0] : ''}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ marginBottom:'28px' }}>
                    <label style={{ display:'block', fontSize:'12px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Full Address</label>
                    <textarea className="pinp" style={{...inp, height:'80px', resize:'vertical'}} placeholder="House name, street, locality..."
                      value={form.customer_address} onChange={e => setForm({...form, customer_address: e.target.value})} />
                  </div>

                  <button onClick={() => {
                    if (!form.customer_name || !form.customer_phone) { setError('Name and phone number are required'); return; }
                    if (form.customer_phone.length !== 10) { setError('Enter a valid 10-digit phone number'); return; }
                    setError(''); setStep(2);
                  }} style={{ width:'100%', padding:'13px', background:primary, color:'white', border:'none', borderRadius:'10px', fontSize:'14px', fontWeight:'700', cursor:'pointer', transition:'all .2s' }}
                    className="snbtn">
                    Continue →
                  </button>
                </div>
              ) : (
                <div>
                  <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'24px' }}>
                    <button onClick={() => setStep(1)} style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:'8px', padding:'8px 14px', fontSize:'13px', cursor:'pointer', color:'#64748b' }}>← Back</button>
                    <div>
                      <h2 style={{ margin:0, fontSize:'20px', fontWeight:'700', color:'#0f172a' }}>Issue Details</h2>
                      <p style={{ margin:0, fontSize:'13px', color:'#64748b' }}>Describe your problem so we can help faster</p>
                    </div>
                  </div>

                  <div style={{ marginBottom:'20px' }}>
                    <label style={{ display:'block', fontSize:'12px', fontWeight:'700', color:'#475569', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Issue Category *</label>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                      {categories.map((c,i) => (
                        <div key={i} className={`pcat${form.category === c ? ' sel' : ''}`} onClick={() => setForm({...form, category: c})}
                          style={{ padding:'10px 14px', border:'1.5px solid #e2e8f0', borderRadius:'8px', fontSize:'13px', cursor:'pointer', transition:'all .15s', fontWeight: form.category === c ? '600' : '400', color: form.category === c ? primary : '#475569', background: form.category === c ? `${primary}08` : 'white' }}>
                          {c}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom:'20px' }}>
                    <label style={{ display:'block', fontSize:'12px', fontWeight:'700', color:'#475569', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Priority Level</label>
                    <div style={{ display:'flex', gap:'10px' }}>
                      {priorities.map(p => (
                        <div key={p.value} className={`ppri${form.priority === p.value ? ' sel' : ''}`} onClick={() => setForm({...form, priority: p.value})}
                          style={{ flex:1, padding:'12px', border:`1.5px solid ${form.priority === p.value ? p.color : '#e2e8f0'}`, borderRadius:'10px', cursor:'pointer', textAlign:'center', transition:'all .15s', background: form.priority === p.value ? p.color + '10' : 'white' }}>
                          <div style={{ fontSize:'13px', fontWeight:'700', color: form.priority === p.value ? p.color : '#1e293b', marginBottom:'2px' }}>{p.label}</div>
                          <div style={{ fontSize:'11px', color:'#94a3b8' }}>{p.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom:'28px' }}>
                    <label style={{ display:'block', fontSize:'12px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Describe Your Issue *</label>
                    <textarea className="pinp" style={{...inp, height:'120px', resize:'vertical'}} placeholder="Please describe the problem in detail - when it started, what you've tried, etc."
                      value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
                  </div>

                  <button onClick={handleSubmit} disabled={submitting} style={{ width:'100%', padding:'13px', background:primary, color:'white', border:'none', borderRadius:'10px', fontSize:'14px', fontWeight:'700', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1, transition:'all .2s' }}
                    className="snbtn">
                    {submitting ? '⏳ Submitting...' : '✓ Submit Complaint'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right sidebar */}
          <div>
            {/* Info card */}
            <div style={{ background:'white', borderRadius:'16px', padding:'24px', boxShadow:'0 4px 24px rgba(0,0,0,0.06)', marginBottom:'16px' }}>
              <h3 style={{ margin:'0 0 16px', fontSize:'15px', fontWeight:'700', color:'#0f172a' }}>What happens next?</h3>
              {[
                { icon:'📝', title:'Ticket Created', desc:'You receive a ticket number instantly' },
                { icon:'📞', title:'Team Notified', desc:'Our support team is alerted immediately' },
                { icon:'🔧', title:'Issue Resolved', desc:'Technician contacts you within 24 hours' },
              ].map((s,i) => (
                <div key={i} style={{ display:'flex', gap:'12px', marginBottom: i < 2 ? '16px' : 0, paddingBottom: i < 2 ? '16px' : 0, borderBottom: i < 2 ? '1px solid #f1f5f9' : 'none' }}>
                  <div style={{ width:'36px', height:'36px', background:`${primary}10`, borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px', flexShrink:0 }}>{s.icon}</div>
                  <div>
                    <p style={{ margin:'0 0 2px', fontSize:'13px', fontWeight:'600', color:'#1e293b' }}>{s.title}</p>
                    <p style={{ margin:0, fontSize:'12px', color:'#94a3b8' }}>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact card */}
            <div style={{ background:`${primary}`, borderRadius:'16px', padding:'24px' }}>
              <h3 style={{ margin:'0 0 8px', fontSize:'15px', fontWeight:'700', color:'white' }}>Need urgent help?</h3>
              <p style={{ margin:'0 0 16px', fontSize:'13px', color:'rgba(255,255,255,.7)', lineHeight:1.5 }}>For critical issues, contact your nearest LCO directly.</p>
              <div style={{ background:'rgba(255,255,255,.1)', borderRadius:'10px', padding:'12px 16px' }}>
                <p style={{ margin:0, fontSize:'13px', color:'rgba(255,255,255,.8)' }}>💬 WhatsApp Support</p>
                <p style={{ margin:'4px 0 0', fontSize:'15px', fontWeight:'700', color:'white' }}>+91 85899 99103</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ background:'#0d1f15', borderTop:'1px solid rgba(255,255,255,0.06)', padding:'24px' }}>
        <div style={{ maxWidth:'960px', margin:'0 auto', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'12px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
            <p style={{ margin:0, fontSize:'12px', color:'rgba(255,255,255,.4)' }}>@{new Date().getFullYear()} Pradesika Digital Internet and Communication LTD</p>
          </div>
          <div style={{ display:'flex', gap:'16px' }}>
            {['Privacy Policy','Terms of Service','Contact Us'].map((l,i) => (
              <span key={i} style={{ fontSize:'12px', color:'rgba(255,255,255,.3)', cursor:'pointer' }}>{l}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicComplaint;
